/**
 * Phase 4 §5 (Component 16) — Worker availability resolution.
 *
 * Pure module. Given a worker's declared availability windows + a shift
 * date+time, returns the worker's availability status:
 *   - 'AVAILABLE'   — explicit AVAILABLE declaration covers the slot
 *   - 'PREFERRED'   — explicit PREFERRED declaration covers the slot
 *   - 'UNAVAILABLE' — explicit UNAVAILABLE declaration covers the slot
 *   - 'UNKNOWN'     — no declaration covers the slot (default — not blocking)
 *
 * Patterns supported:
 *   - ONE_TIME (effective_from/to as the date range)
 *   - RECURRING_WEEKLY (day_of_week + optional start/end time-of-day)
 *   - RECURRING_FORTNIGHTLY (effective_from-anchored week parity)
 *   - RECURRING_MONTHLY (calendar-day-of-month, naive)
 *   - RRULE (deferred — requires rrule.js dep; returns UNKNOWN with a warning
 *     so the v1 ship doesn't add a heavy dep)
 *
 * Conflict resolution: when multiple declarations match a slot, UNAVAILABLE
 * wins over PREFERRED wins over AVAILABLE. The roster gate
 * `WORKER_NOT_AVAILABLE` fires only on UNAVAILABLE.
 *
 * Used by:
 *   - roster-gate-detection.ts (Phase 4 Component 15) — `WORKER_NOT_AVAILABLE`
 *   - open-shift eligibility (Phase 4 Component 17 — Session B)
 */

export type AvailabilityType = 'AVAILABLE' | 'UNAVAILABLE' | 'PREFERRED';
export type AvailabilityResolution = AvailabilityType | 'UNKNOWN';
export type AvailabilityPattern =
  | 'ONE_TIME'
  | 'RECURRING_WEEKLY'
  | 'RECURRING_FORTNIGHTLY'
  | 'RECURRING_MONTHLY'
  | 'RRULE';

export interface WorkerAvailabilityWindow {
  id: string;
  staffId: string;
  availabilityType: AvailabilityType;
  pattern: AvailabilityPattern;
  /** 0=Sunday, 1=Monday, ..., 6=Saturday. Required for RECURRING_WEEKLY. */
  dayOfWeek?: number | null;
  /** 'HH:MM' (24h) — null = full day. */
  startTime?: string | null;
  endTime?: string | null;
  /** YYYY-MM-DD. */
  effectiveFrom: string;
  effectiveTo?: string | null;
  /** iCal RRULE (RFC 5545) — only used for pattern='RRULE'. */
  recurrenceRule?: string | null;
  status: 'ACTIVE' | 'EXPIRED' | 'REVOKED';
}

export interface ResolveAvailabilityInput {
  staffId: string;
  /** Shift start as ISO datetime. The resolver looks at the date and the
   *  time-of-day in the tenant's timezone. */
  shiftStart: string;
  shiftEnd: string;
  /** Tenant TZ for day-of-week + hour-of-day classification. */
  timezone?: string;
  /** All ACTIVE windows for the worker (caller filters by staff_id + status). */
  windows: WorkerAvailabilityWindow[];
}

function dateInTz(date: Date, tz: string): string {
  // ISO yyyy-mm-dd in tz
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
  return parts; // en-CA gives yyyy-mm-dd
}

function timeOfDayInTz(date: Date, tz: string): string {
  // Returns 'HH:MM' (24h) in tz
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

function timeStringToMinutes(s: string): number {
  // 'HH:MM' or 'HH:MM:SS' → minutes since midnight
  const [hStr, mStr] = s.split(':');
  return parseInt(hStr ?? '0', 10) * 60 + parseInt(mStr ?? '0', 10);
}

/** Inclusive date range check (YYYY-MM-DD). */
function dateInEffectiveRange(date: string, from: string, to: string | null | undefined): boolean {
  if (date < from) return false;
  if (to != null && date > to) return false;
  return true;
}

function previousIsoDate(date: string): string {
  const value = new Date(`${date}T00:00:00.000Z`);
  value.setUTCDate(value.getUTCDate() - 1);
  return `${value.getUTCFullYear()}-${String(value.getUTCMonth() + 1).padStart(2, '0')}-${String(value.getUTCDate()).padStart(2, '0')}`;
}

function patternMatchesDate(window: WorkerAvailabilityWindow, date: string): boolean {
  if (!dateInEffectiveRange(date, window.effectiveFrom, window.effectiveTo)) return false;
  if (window.pattern === 'RRULE') return false;
  const candidate = new Date(`${date}T00:00:00.000Z`);
  if (window.pattern === 'RECURRING_WEEKLY')
    return window.dayOfWeek != null && candidate.getUTCDay() === window.dayOfWeek;
  if (window.pattern === 'RECURRING_FORTNIGHTLY') {
    if (window.dayOfWeek == null || candidate.getUTCDay() !== window.dayOfWeek) return false;
    const anchor = new Date(`${window.effectiveFrom}T00:00:00.000Z`);
    const elapsedWeeks = Math.floor((candidate.getTime() - anchor.getTime()) / 604_800_000);
    return elapsedWeeks % 2 === 0;
  }
  if (window.pattern === 'RECURRING_MONTHLY')
    return candidate.getUTCDate() === Number(window.effectiveFrom.slice(8, 10));
  return true;
}

/**
 * Determine whether a single window covers a given shift.
 * Returns true if it does, false otherwise.
 */
function windowCoversShift(
  window: WorkerAvailabilityWindow,
  shiftStart: Date,
  shiftEnd: Date,
  tz: string,
): boolean {
  if (window.status !== 'ACTIVE') return false;

  const shiftDateStr = dateInTz(shiftStart, tz);
  const previousDate = previousIsoDate(shiftDateStr);
  const shiftStartMins = timeStringToMinutes(timeOfDayInTz(shiftStart, tz));
  let shiftEndMins =
    shiftDateStr === dateInTz(shiftEnd, tz)
      ? timeStringToMinutes(timeOfDayInTz(shiftEnd, tz))
      : timeStringToMinutes(timeOfDayInTz(shiftEnd, tz)) + 24 * 60;
  if (shiftEndMins <= shiftStartMins) shiftEndMins += 24 * 60;
  if (!window.startTime && !window.endTime) return patternMatchesDate(window, shiftDateStr);

  const windowStart = window.startTime ? timeStringToMinutes(window.startTime) : 0;
  let windowEnd = window.endTime ? timeStringToMinutes(window.endTime) : 24 * 60;
  const ranges: Array<[number, number]> = [];
  if (windowEnd <= windowStart) {
    windowEnd += 24 * 60;
    if (patternMatchesDate(window, shiftDateStr)) ranges.push([windowStart, windowEnd]);
    if (patternMatchesDate(window, previousDate))
      ranges.push([windowStart - 24 * 60, windowEnd - 24 * 60]);
  } else if (patternMatchesDate(window, shiftDateStr)) {
    ranges.push([windowStart, windowEnd]);
  }
  return ranges.some(
    ([rangeStart, rangeEnd]) => shiftStartMins < rangeEnd && shiftEndMins > rangeStart,
  );
}

const TYPE_PRIORITY: Record<AvailabilityType, number> = {
  UNAVAILABLE: 3,
  PREFERRED: 2,
  AVAILABLE: 1,
};

/**
 * Resolve the worker's availability for a given shift.
 *
 * When multiple matching windows exist, conflict resolution is:
 *   UNAVAILABLE > PREFERRED > AVAILABLE
 *
 * Returns 'UNKNOWN' if no window matches.
 */
export function resolveWorkerAvailability(input: ResolveAvailabilityInput): AvailabilityResolution {
  const tz = input.timezone ?? 'Australia/Sydney';
  const shiftStart = new Date(input.shiftStart);
  const shiftEnd = new Date(input.shiftEnd);

  let strongest: AvailabilityType | null = null;
  let strongestPriority = 0;

  for (const win of input.windows) {
    if (win.staffId !== input.staffId) continue;
    if (!windowCoversShift(win, shiftStart, shiftEnd, tz)) continue;
    const priority = TYPE_PRIORITY[win.availabilityType];
    if (priority > strongestPriority) {
      strongest = win.availabilityType;
      strongestPriority = priority;
    }
  }

  return strongest ?? 'UNKNOWN';
}

/**
 * Convenience: returns true if the worker has explicitly declared
 * UNAVAILABLE for the given shift.
 */
export function isWorkerUnavailable(input: ResolveAvailabilityInput): boolean {
  return resolveWorkerAvailability(input) === 'UNAVAILABLE';
}
